import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthProvider';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  User, Lock, Mail, Shield, LogIn, UserPlus, 
  Eye, EyeOff, HelpCircle, AlertCircle, X, Chrome
} from 'lucide-react';

const SECURITY_QUESTIONS = [
  "What is your mother's maiden name?",
  "What was the name of your first pet?",
  "What city were you born in?",
  "What is your favorite movie?",
  "What was your childhood nickname?",
  "What is your favorite color?"
];

export const AuthModal = ({ isOpen, onClose, onSuccess, onBeforeGoogleAuth }) => {
  const { login, register, loginWithGoogle, continueAsGuest, getSecurityQuestion, recoverPassword } = useAuth();
  
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showRecoveryPassword, setShowRecoveryPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState(1);
  const [securityQuestion, setSecurityQuestion] = useState('');
  
  // Login form
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regSecurityQuestion, setRegSecurityQuestion] = useState('');
  const [regSecurityAnswer, setRegSecurityAnswer] = useState('');
  const [regSignatureBlock, setRegSignatureBlock] = useState('');
  
  // Recovery form
  const [recoveryUsername, setRecoveryUsername] = useState('');
  const [recoveryAnswer, setRecoveryAnswer] = useState('');
  const [recoveryNewPassword, setRecoveryNewPassword] = useState('');

  if (!isOpen) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginUsername, loginPassword);
      toast.success('Welcome back!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (regPassword !== regConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (!regSecurityQuestion || !regSecurityAnswer) {
      toast.error('Security question and answer are required for password recovery');
      return;
    }
    
    setIsLoading(true);
    try {
      await register({
        username: regUsername,
        password: regPassword,
        name: regName || regUsername,
        email: regEmail || null,
        security_question: regSecurityQuestion,
        security_answer: regSecurityAnswer,
        signature_block: regSignatureBlock || null
      });
      toast.success('Account created successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Save guest session before OAuth redirect
    if (onBeforeGoogleAuth) {
      onBeforeGoogleAuth();
    }
    // Set flag to restore session after redirect
    localStorage.setItem('writeright_pending_restore', 'true');
    loginWithGoogle();
  };

  const handleGuestMode = () => {
    continueAsGuest();
    toast.info('Continuing as guest. Your history may not persist across sessions.', {
      duration: 5000
    });
    onSuccess?.();
    onClose();
  };

  const handleStartRecovery = async () => {
    if (!recoveryUsername) {
      toast.error('Please enter your username');
      return;
    }
    setIsLoading(true);
    try {
      const { security_question } = await getSecurityQuestion(recoveryUsername);
      setSecurityQuestion(security_question);
      setRecoveryStep(2);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoverPassword = async () => {
    if (!recoveryAnswer || !recoveryNewPassword) {
      toast.error('Please fill all fields');
      return;
    }
    setIsLoading(true);
    try {
      await recoverPassword(recoveryUsername, recoveryAnswer, recoveryNewPassword);
      toast.success('Password updated! You are now logged in.');
      setShowRecovery(false);
      setRecoveryStep(1);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetRecovery = () => {
    setShowRecovery(false);
    setRecoveryStep(1);
    setRecoveryUsername('');
    setRecoveryAnswer('');
    setRecoveryNewPassword('');
    setShowRecoveryPassword(false);
    setSecurityQuestion('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl text-foreground font-bold flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              {showRecovery ? 'Password Recovery' : 'Sign In to PCM+'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
          {!showRecovery && (
            <CardDescription className="text-muted-foreground">
              Sign in to save your history and templates, or continue as guest
            </CardDescription>
          )}
        </CardHeader>
        
        <CardContent>
          {showRecovery ? (
            <div className="space-y-4">
              {recoveryStep === 1 ? (
                <>
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      value={recoveryUsername}
                      onChange={(e) => setRecoveryUsername(e.target.value)}
                      placeholder="Enter your username"
                      className="bg-input border-border"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={resetRecovery} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleStartRecovery} disabled={isLoading} className="flex-1">
                      {isLoading ? 'Loading...' : 'Next'}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-3 bg-secondary rounded-lg border border-border">
                    <Label className="text-xs text-muted-foreground">Security Question</Label>
                    <p className="text-sm text-foreground mt-1">{securityQuestion}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Your Answer</Label>
                    <Input
                      type="text"
                      value={recoveryAnswer}
                      onChange={(e) => setRecoveryAnswer(e.target.value)}
                      placeholder="Enter your security answer"
                      className="bg-input border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10 pointer-events-none" />
                      <Input
                        type={showRecoveryPassword ? 'text' : 'password'}
                        value={recoveryNewPassword}
                        onChange={(e) => setRecoveryNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        className="pl-10 pr-12 bg-input border-border"
                        data-testid="recovery-new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowRecoveryPassword(!showRecoveryPassword)}
                        aria-label={showRecoveryPassword ? "Hide password" : "Show password"}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center rounded-md text-foreground/80 hover:text-foreground hover:bg-foreground/10 transition-colors"
                        data-testid="recovery-password-toggle"
                      >
                        {showRecoveryPassword ? <EyeOff className="w-4 h-4" strokeWidth={2.25} /> : <Eye className="w-4 h-4" strokeWidth={2.25} />}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={resetRecovery} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleRecoverPassword} disabled={isLoading} className="flex-1">
                      {isLoading ? 'Updating...' : 'Reset Password'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Google Sign In */}
              <Button 
                onClick={handleGoogleLogin}
                variant="outline" 
                className="w-full mb-2 h-11 border-border hover:bg-secondary"
                data-testid="google-signin-btn"
              >
                <Chrome className="w-5 h-5 mr-2" />
                Sign in with Google
              </Button>
              {/* Mar 2, 2026 — Provider was auto-signed into the WRONG Gmail
                  (`stewartlakia@gmail.com` instead of `dnpstewartlakia@gmail.com`)
                  because Emergent-managed OAuth uses the browser's default
                  Google session. We cannot inject `prompt=select_account`
                  into Emergent's OAuth URL, so we surface a helper hint. */}
              <p className="text-[11px] text-muted-foreground mb-4 leading-relaxed">
                Wrong Google account?{' '}
                <a
                  href="https://accounts.google.com/logout"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-primary hover:text-primary/80"
                  data-testid="google-logout-hint"
                >
                  Sign out of Google here
                </a>{' '}
                first, then click "Sign in with Google" again. Or open the app in Incognito.
              </p>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="login">Sign In</TabsTrigger>
                  <TabsTrigger value="register">Create Account</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-username">Username</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="login-username"
                          value={loginUsername}
                          onChange={(e) => setLoginUsername(e.target.value)}
                          placeholder="Enter username"
                          className="pl-10 bg-input border-border"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10 pointer-events-none" />
                        <Input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          placeholder="Enter password"
                          className="pl-10 pr-12 bg-input border-border"
                          required
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center rounded-md text-foreground/80 hover:text-foreground hover:bg-foreground/10 transition-colors"
                          data-testid="login-password-toggle"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={2.25} /> : <Eye className="w-4 h-4" strokeWidth={2.25} />}
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowRecovery(true)}
                      className="text-xs text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                    <Button type="submit" disabled={isLoading} className="w-full">
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="reg-username">Username *</Label>
                      <Input
                        id="reg-username"
                        value={regUsername}
                        onChange={(e) => setRegUsername(e.target.value)}
                        placeholder="Choose a username"
                        className="bg-input border-border"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="reg-password">Password *</Label>
                        <Input
                          id="reg-password"
                          type="password"
                          value={regPassword}
                          onChange={(e) => setRegPassword(e.target.value)}
                          placeholder="Password"
                          className="bg-input border-border"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="reg-confirm">Confirm *</Label>
                        <Input
                          id="reg-confirm"
                          type="password"
                          value={regConfirmPassword}
                          onChange={(e) => setRegConfirmPassword(e.target.value)}
                          placeholder="Confirm"
                          className="bg-input border-border"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-name">Display Name</Label>
                      <Input
                        id="reg-name"
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        placeholder="Your name (optional)"
                        className="bg-input border-border"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-email">Email (optional)</Label>
                      <Input
                        id="reg-email"
                        type="email"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        placeholder="email@example.com"
                        className="bg-input border-border"
                      />
                    </div>
                    <div className="p-3 bg-secondary/50 rounded-lg border border-border space-y-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <HelpCircle className="w-3.5 h-3.5" />
                        For password recovery (no email relay)
                      </div>
                      <div className="space-y-2">
                        <Label>Security Question *</Label>
                        <Select value={regSecurityQuestion} onValueChange={setRegSecurityQuestion}>
                          <SelectTrigger className="bg-input border-border">
                            <SelectValue placeholder="Select a question" />
                          </SelectTrigger>
                          <SelectContent>
                            {SECURITY_QUESTIONS.map(q => (
                              <SelectItem key={q} value={q}>{q}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Security Answer *</Label>
                        <Input
                          value={regSecurityAnswer}
                          onChange={(e) => setRegSecurityAnswer(e.target.value)}
                          placeholder="Your answer"
                          className="bg-input border-border"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Signature Block (optional)</Label>
                      <Textarea
                        value={regSignatureBlock}
                        onChange={(e) => setRegSignatureBlock(e.target.value)}
                        placeholder="Your default signature for letters (can edit later)"
                        className="bg-input border-border h-20 text-xs"
                      />
                    </div>
                    <Button type="submit" disabled={isLoading} className="w-full">
                      {isLoading ? 'Creating...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              <div className="mt-4 pt-4 border-t border-border">
                <Button 
                  variant="ghost" 
                  onClick={handleGuestMode}
                  className="w-full text-muted-foreground hover:text-foreground"
                >
                  Continue as Guest
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-2 flex items-center justify-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Guest history may not persist across sessions
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthModal;
